/**
 * Main App component with routing configuration.
 *
 * Route structure (ACT-INDEEP-MOBILE-AUTH-PIXEL-POLISH01):
 * - No layout route: Auth routes that render without app shell chrome
 *   - `/`: Auth-aware entry (RootEntry → MobileLoginPage for anonymous)
 *   - `/login`, `/login/mobile`: Redirect to `/`
 * - App layout route (`<AppShellRoute>`): Routes that include the app header/footer
 *   - `/register`, `/onboarding`, `/paywall`, `/contact` (public app routes)
 *   - `/discover`, `/card/:id`, `/intro-request` (sponsor-gated)
 *   - `/account`, `/privacy`, `/report`, `/profile-completion` (auth-required)
 *   - `/chat`, `/chat/:channelId` (auth-required)
 *   - `/admin` (admin role required)
 *   - `/sponsor-profile` (authenticated sponsor users)
 *   - `/feed` (auth-required, S011 host feed)
 *   - `/feed/compose` (auth-required)
 *   - `/feed/:postId/comments` (auth-required, S011 comments)
 */
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { SponsorAccessRoute } from '@/components/ProtectedRoute';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { AdminRoute } from '@/components/AdminRoute';
import { RoutePaths } from '@/routes';
import { AuthBootstrapProvider } from '@/hooks/useAuthBootstrap';
import {
  RootEntry,
  RegisterPage,
  LoginVerifyPage,
  VerifyPage,
  OnboardingPage,
  PaywallPage,
  DiscoverPage,
  IntroRequestPage,
  FriendInboxPage,
  FeedComposePage,
  FeedPage,
  AccountPage,
  ProfileCompletionPage,
  ProfileQuestionnairePage,
  PrivacyPage,
  ReportPage,
  ContactPage,
  ChatListPage,
  ChatChannelPage,
  OpenPersonalChatPage,
  AdminModerationPage,
  SponsorProfilePage,
  ProfileDetailPage,
  CommentsPage,
} from '@/pages';

/**
 * App shell route adapter.
 * Wraps Layout with React Router's Outlet for nested route rendering.
 * React Router layout routes render child routes through <Outlet />.
 */
function AppShellRoute() {
  return (
    <Layout>
      <Outlet />
    </Layout>
  );
}

function App() {
  return (
    <AuthBootstrapProvider>
      <Routes>
        {/* No-layout routes: Auth routes that render without app shell chrome */}

        {/* Root `/` - auth-aware entry point (ACT-INDEEP-ROOT-MOBILE-LOGIN01) */}
        <Route path={RoutePaths.HOME} element={<RootEntry />} />

        {/* Legacy login routes - redirect to `/` */}
        <Route path={RoutePaths.LOGIN} element={<Navigate to={RoutePaths.HOME} replace />} />
        <Route path={RoutePaths.MOBILE_LOGIN} element={<Navigate to={RoutePaths.HOME} replace />} />

        {/* App layout route: All other routes with the app shell */}
        <Route element={<AppShellRoute />}>
          {/* Public app routes */}
          <Route path={RoutePaths.REGISTER} element={<RegisterPage />} />
          <Route path={RoutePaths.LOGIN_VERIFY} element={<LoginVerifyPage />} />
          <Route path={RoutePaths.VERIFY} element={<VerifyPage />} />
          <Route path={RoutePaths.ONBOARDING} element={<OnboardingPage />} />
          <Route path={RoutePaths.PAYWALL} element={<PaywallPage />} />
          <Route path={RoutePaths.CONTACT} element={<ContactPage />} />

          {/* Sponsor-gated routes - require sponsor_access */}
          <Route
            path={RoutePaths.DISCOVER}
            element={
              <SponsorAccessRoute requiresSponsorAccess>
                <DiscoverPage />
              </SponsorAccessRoute>
            }
          />
          <Route
            path={RoutePaths.CARD_DETAIL}
            element={
              <ProtectedRoute>
                <ProfileDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={RoutePaths.INTRO_REQUEST}
            element={
              <SponsorAccessRoute requiresSponsorAccess>
                <IntroRequestPage />
              </SponsorAccessRoute>
            }
          />

          {/* G1 Friend-side intro-request inbox (U1-U5) */}
          <Route
            path={RoutePaths.FRIEND_INBOX}
            element={
              <ProtectedRoute>
                <FriendInboxPage />
              </ProtectedRoute>
            }
          />

          {/* Feed routes */}
          <Route
            path={RoutePaths.FEED}
            element={
              <ProtectedRoute>
                <FeedPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={RoutePaths.FEED_COMPOSE}
            element={
              <ProtectedRoute>
                <FeedComposePage />
              </ProtectedRoute>
            }
          />
          <Route
            path={RoutePaths.FEED_COMMENTS}
            element={
              <ProtectedRoute>
                <CommentsPage />
              </ProtectedRoute>
            }
          />

          {/* Authenticated routes */}
          <Route
            path={RoutePaths.ACCOUNT}
            element={
              <ProtectedRoute>
                <AccountPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={RoutePaths.PROFILE_COMPLETION}
            element={
              <ProtectedRoute>
                <ProfileCompletionPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={RoutePaths.PROFILE_QUESTIONNAIRE}
            element={
              <ProtectedRoute>
                <ProfileQuestionnairePage />
              </ProtectedRoute>
            }
          />
          <Route
            path={RoutePaths.PRIVACY}
            element={
              <ProtectedRoute>
                <PrivacyPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={RoutePaths.REPORT}
            element={
              <ProtectedRoute>
                <ReportPage />
              </ProtectedRoute>
            }
          />

          {/* Chat routes */}
          <Route
            path={RoutePaths.CHAT}
            element={
              <ProtectedRoute>
                <ChatListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={RoutePaths.CHAT_CHANNEL}
            element={
              <ProtectedRoute>
                <ChatChannelPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={RoutePaths.CHAT_OPEN_PERSONAL}
            element={
              <ProtectedRoute>
                <OpenPersonalChatPage />
              </ProtectedRoute>
            }
          />

          {/* Admin routes - require explicit admin role check via AdminRoute */}
          <Route
            path={RoutePaths.ADMIN}
            element={
              <AdminRoute>
                <AdminModerationPage />
              </AdminRoute>
            }
          />

          {/* Sponsor profile route - requires sponsor registration kind (authenticated) */}
          <Route
            path={RoutePaths.SPONSOR_PROFILE}
            element={
              <ProtectedRoute>
                <SponsorProfilePage />
              </ProtectedRoute>
            }
          />
        </Route>
      </Routes>
    </AuthBootstrapProvider>
  );
}

export default App;